// US-REPORT-03 -- Chore completion trends.

import {
  type AccountListEntry,
  type ChoreTrendsResult,
  accountsList,
  reportChoreTrends,
} from '@chorepop/core/client';
import { type Component, For, Show, createResource, createSignal } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';

type Props = {
  sessionId: string;
  currency: string;
  onBack: () => void;
};

export const ChoreTrendsScreen: Component<Props> = (props) => {
  const [children] = createResource(async () => {
    const r = await accountsList();
    return r.ok ? r.value.filter((a) => a.roleCode === 'child') : ([] as AccountListEntry[]);
  });

  const now = new Date();
  const [childId, setChildId] = createSignal<number | null>(null);
  const [fromDate, setFromDate] = createSignal(
    new Date(now.getTime() - 90 * 86400000).toISOString().slice(0, 10),
  );
  const [toDate, setToDate] = createSignal(now.toISOString().slice(0, 10));

  const [trends, { refetch }] = createResource(
    () => ({ cid: childId(), from: fromDate(), to: toDate() }),
    async ({ cid, from, to }) => {
      const r = await reportChoreTrends({
        sessionId: props.sessionId,
        ...(cid ? { childAccountId: cid } : {}),
        fromDate: from,
        toDate: to,
      });
      return r.ok ? r.value : null;
    },
  );

  const maxWeekTotal = (data: ChoreTrendsResult) =>
    Math.max(1, ...data.weeks.map((w) => w.completed + w.rejected + w.missed + w.pending));

  const barPct = (val: number, max: number) => `${Math.round((val / max) * 100)}%`;

  return (
    <ActionScreen eyebrow="Completion report" title="Chore trends" onBack={props.onBack}>
      <div class="statement-controls action-form">
        <select
          value={childId() ?? ''}
          onChange={(e) => setChildId(Number(e.currentTarget.value) || null)}
        >
          <option value="">All children</option>
          <For each={children() ?? []}>
            {(c: AccountListEntry) => <option value={c.accountId}>{c.displayName}</option>}
          </For>
        </select>
        <input type="date" value={fromDate()} onInput={(e) => setFromDate(e.currentTarget.value)} />
        <span style="color: var(--ink-2); align-self: center;">to</span>
        <input type="date" value={toDate()} onInput={(e) => setToDate(e.currentTarget.value)} />
        <button type="button" class="ghost" onClick={() => void refetch()}>
          Refresh
        </button>
      </div>

      <Show when={trends.loading}>
        <p style="padding: var(--space-4);">Loading...</p>
      </Show>

      <Show when={!trends.loading && trends()}>
        {(data: () => ChoreTrendsResult) => (
          <div style="padding: 0 var(--space-4);">
            {/* Summary row */}
            <div class="trends-summary-row">
              <div class="trends-stat">
                <span class="trends-stat-value">{data().completionRatePct}%</span>
                <span class="trends-stat-label">Completion rate</span>
              </div>
              <div class="trends-stat">
                <span class="trends-stat-value" style="color: #1d6131;">
                  {data().totalCompleted}
                </span>
                <span class="trends-stat-label">Completed</span>
              </div>
              <div class="trends-stat">
                <span class="trends-stat-value" style="color: var(--color-danger, #c0392b);">
                  {data().totalRejected}
                </span>
                <span class="trends-stat-label">Rejected</span>
              </div>
              <div class="trends-stat">
                <span class="trends-stat-value" style="color: var(--ink-3);">
                  {data().totalMissed}
                </span>
                <span class="trends-stat-label">Missed</span>
              </div>
            </div>

            {/* Weekly bar chart */}
            <Show when={data().weeks.length > 0}>
              <div class="trends-chart">
                <For each={data().weeks}>
                  {(week) => {
                    const max = maxWeekTotal(data());
                    return (
                      <div class="trends-week-col">
                        <div class="trends-bar-stack">
                          <Show when={week.completed > 0}>
                            <div
                              class="trends-bar trends-bar--done"
                              style={`height: ${barPct(week.completed, max)}`}
                              title={`${week.completed} completed`}
                            />
                          </Show>
                          <Show when={week.rejected > 0}>
                            <div
                              class="trends-bar trends-bar--rejected"
                              style={`height: ${barPct(week.rejected, max)}`}
                              title={`${week.rejected} rejected`}
                            />
                          </Show>
                          <Show when={week.missed > 0}>
                            <div
                              class="trends-bar trends-bar--missed"
                              style={`height: ${barPct(week.missed, max)}`}
                              title={`${week.missed} missed`}
                            />
                          </Show>
                        </div>
                        <span class="trends-week-label">{week.weekStart.slice(5)}</span>
                      </div>
                    );
                  }}
                </For>
              </div>
              {/* Legend */}
              <div class="trends-legend">
                <span class="trends-legend-item">
                  <span class="trends-dot trends-dot--done" />
                  Completed
                </span>
                <span class="trends-legend-item">
                  <span class="trends-dot trends-dot--rejected" />
                  Rejected
                </span>
                <span class="trends-legend-item">
                  <span class="trends-dot trends-dot--missed" />
                  Missed
                </span>
              </div>
            </Show>
            <Show when={data().weeks.length === 0}>
              <p style="color: var(--ink-2); margin-top: var(--space-4);">
                No chore data for this period.
              </p>
            </Show>
          </div>
        )}
      </Show>
    </ActionScreen>
  );
};
